import { Container, Sprite, Texture } from 'pixi.js';
import { DESIGN, type QualityProfile } from '../core/runtime.js';

interface Star {
  sprite: Sprite;
  baseAlpha: number;
  twinkleSpeed: number;
  phase: number;
  parallax: number;
  baseX: number;
}

/**
 * Deep-space backdrop.
 *
 * Three parallax layers: a graded void, two additive nebula clouds that rotate
 * against each other, and a star field that twinkles and drifts. All of it is
 * generated at runtime from canvases, so the overlay ships no background art
 * and can be re-tinted per phase without swapping assets.
 *
 * It sits outside the camera transform and does its own, much slower parallax -
 * a background that moves with the camera reads as a flat painted wall.
 */
export class GalaxyBackground {
  readonly view = new Container();

  private readonly stars: Star[] = [];
  private readonly nebulaA: Sprite;
  private readonly nebulaB: Sprite;
  private elapsed = 0;
  private targetTint = 0x2a6ad6;

  constructor(quality: QualityProfile) {
    this.view.eventMode = 'none';

    const voidLayer = new Sprite(createVoidTexture());
    voidLayer.width = DESIGN.width;
    voidLayer.height = DESIGN.height;

    const nebulaTexture = createNebulaTexture();

    this.nebulaA = new Sprite(nebulaTexture);
    this.nebulaA.anchor.set(0.5);
    this.nebulaA.position.set(DESIGN.width * 0.38, DESIGN.height * 0.42);
    this.nebulaA.scale.set(2.5);
    this.nebulaA.alpha = 0.5;
    this.nebulaA.blendMode = 'add';
    this.nebulaA.tint = 0x2f6fd0;

    this.nebulaB = new Sprite(nebulaTexture);
    this.nebulaB.anchor.set(0.5);
    this.nebulaB.position.set(DESIGN.width * 0.68, DESIGN.height * 0.6);
    this.nebulaB.scale.set(3.1);
    this.nebulaB.alpha = 0.32;
    this.nebulaB.blendMode = 'add';
    this.nebulaB.tint = 0x7a3fd0;
    this.nebulaB.rotation = Math.PI * 0.4;

    const starLayer = new Container();
    starLayer.eventMode = 'none';
    const starTexture = createStarTexture();

    for (let i = 0; i < quality.starCount; i++) {
      const sprite = new Sprite(starTexture);
      sprite.anchor.set(0.5);
      const size = 1.4 + Math.random() * 3.4;
      sprite.width = size;
      sprite.height = size;
      sprite.x = Math.random() * DESIGN.width;
      sprite.y = Math.random() * DESIGN.height;
      sprite.blendMode = 'add';

      // A handful of stars get a warm tint; a pure white field looks synthetic.
      sprite.tint = Math.random() < 0.14 ? 0xffd9a0 : Math.random() < 0.2 ? 0xa8d4ff : 0xffffff;

      const baseAlpha = 0.28 + Math.random() * 0.72;
      sprite.alpha = baseAlpha;
      starLayer.addChild(sprite);

      this.stars.push({
        sprite,
        baseAlpha,
        twinkleSpeed: 0.6 + Math.random() * 2.4,
        phase: Math.random() * Math.PI * 2,
        parallax: 0.15 + Math.random() * 0.85,
        baseX: sprite.x,
      });
    }

    this.view.addChild(voidLayer, this.nebulaA, this.nebulaB, starLayer);
  }

  /** Shift the nebula colour, e.g. to a continent's accent during its segment. */
  setAccent(color: number): void {
    this.targetTint = color;
  }

  update(deltaSeconds: number): void {
    this.elapsed += deltaSeconds;

    this.nebulaA.rotation += deltaSeconds * 0.008;
    this.nebulaB.rotation -= deltaSeconds * 0.005;
    this.nebulaA.tint = approachTint(this.nebulaA.tint as number, this.targetTint, deltaSeconds);

    const drift = this.elapsed * 4;
    for (const star of this.stars) {
      star.sprite.alpha =
        star.baseAlpha * (0.55 + 0.45 * Math.sin(this.elapsed * star.twinkleSpeed + star.phase));
      star.sprite.x = wrap(star.baseX - drift * star.parallax, DESIGN.width);
    }
  }
}

function wrap(value: number, max: number): number {
  const m = value % max;
  return m < 0 ? m + max : m;
}

/** Nudge a packed RGB toward a target. Per-channel so hues cross smoothly. */
function approachTint(current: number, target: number, deltaSeconds: number): number {
  const k = Math.min(1, deltaSeconds * 0.6);
  const cr = (current >> 16) & 0xff;
  const cg = (current >> 8) & 0xff;
  const cb = current & 0xff;
  const tr = (target >> 16) & 0xff;
  const tg = (target >> 8) & 0xff;
  const tb = target & 0xff;
  const r = Math.round(cr + (tr - cr) * k);
  const g = Math.round(cg + (tg - cg) * k);
  const b = Math.round(cb + (tb - cb) * k);
  return (r << 16) | (g << 8) | b;
}

function context(width: number, height: number): CanvasRenderingContext2D {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas is unavailable - cannot build the galaxy');
  return ctx;
}

function createVoidTexture(): Texture {
  const ctx = context(256, 256);
  const gradient = ctx.createRadialGradient(128, 108, 20, 128, 128, 190);
  gradient.addColorStop(0, '#0d1b33');
  gradient.addColorStop(0.5, '#070d1c');
  gradient.addColorStop(1, '#03050c');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 256, 256);
  return Texture.from(ctx.canvas);
}

function createNebulaTexture(): Texture {
  const size = 512;
  const ctx = context(size, size);

  for (let i = 0; i < 26; i++) {
    const x = Math.random() * size;
    const y = Math.random() * size;
    const radius = 40 + Math.random() * 150;
    const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
    const alpha = 0.05 + Math.random() * 0.12;
    gradient.addColorStop(0, `rgba(255,255,255,${alpha})`);
    gradient.addColorStop(0.5, `rgba(255,255,255,${alpha * 0.35})`);
    gradient.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, size, size);
  }

  // Fade the edges so the sprite never shows a visible square boundary.
  const mask = ctx.createRadialGradient(
    size / 2,
    size / 2,
    size * 0.18,
    size / 2,
    size / 2,
    size / 2,
  );
  mask.addColorStop(0, 'rgba(0,0,0,0)');
  mask.addColorStop(1, 'rgba(0,0,0,1)');
  ctx.globalCompositeOperation = 'destination-out';
  ctx.fillStyle = mask;
  ctx.fillRect(0, 0, size, size);

  return Texture.from(ctx.canvas);
}

function createStarTexture(): Texture {
  const ctx = context(32, 32);
  const gradient = ctx.createRadialGradient(16, 16, 0, 16, 16, 16);
  gradient.addColorStop(0, 'rgba(255,255,255,1)');
  gradient.addColorStop(0.3, 'rgba(255,255,255,0.6)');
  gradient.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 32, 32);
  return Texture.from(ctx.canvas);
}
